import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FiX, FiTrash2, FiMinus, FiPlus, FiEdit2 } from 'react-icons/fi';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from 'react-i18next';
import { useCart, useCCCart, BYOCartItem } from '../../context/CartContext';
import type { SelectedOption, CCSelectedOption } from '../../context/CartContext';
import CCOptionsEditModal from './CCOptionsEditModal';
import { formatCurrency } from '../../utils/currency';
import { getLocalizedField } from '../../utils/localize';
import type { SupportedLanguage } from '../../i18n';
import { resolveUploadUrl } from '../../utils/imageUrl';

const CakePhotoThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin: 0.25rem 0;
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 100%;
  max-width: 400px;
  background-color: var(--white);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  @media (max-width: 400px) {
    max-width: 100%;
  }
`;

const DrawerHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--gray-medium);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DrawerTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--text-color);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--gray-dark);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  &:hover {
    color: var(--text-color);
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  background-color: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const WarningBannerDismiss = styled.button`
  background: none;
  border: none;
  color: #991b1b;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.75;
  }
`;

const SwipeHint = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    text-align: center;
    padding: 0.5rem;
    background-color: var(--primary-light, #e8f5e9);
    color: var(--primary-color);
    font-size: 0.85rem;
    margin: -1rem -1rem 1rem -1rem;
    border-bottom: 1px solid var(--gray-light);
  }
`;

const CartItem = styled.div<{ $swipeOffset: number; $isDeleting: boolean }>`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--gray-light);
  position: relative;
  transform: translateX(${props => props.$swipeOffset}px);
  transition: ${props => props.$isDeleting ? 'transform 0.3s ease-out, opacity 0.3s ease-out' : 'transform 0.2s ease-out'};
  opacity: ${props => props.$isDeleting ? 0 : 1};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    touch-action: pan-y;
  }
`;

const SwipeDeleteBackground = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(to left, #e74c3c, #c0392b);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 2rem;
  color: white;
  font-weight: bold;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease-out;
  pointer-events: none;

  svg {
    font-size: 1.5rem;
  }
`;

const CartItemContent = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  background-color: var(--white);
  position: relative;
  z-index: 1;
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.h4`
  font-size: 1rem;
  margin-bottom: 0.25rem;
  color: var(--text-color);
`;

const ItemSize = styled.p`
  font-size: 0.9rem;
  color: var(--gray-dark);
  margin-bottom: 0.5rem;
`;

const ItemPrice = styled.p`
  font-size: 1rem;
  font-weight: bold;
  color: var(--primary-color);
`;

const ItemSubtotal = styled.p`
  font-size: 0.9rem;
  color: var(--gray-dark);
  margin-top: 0.25rem;
  font-style: italic;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 0.75rem;
  }
`;

const QuantityButton = styled.button<{ $isLongPressed?: boolean }>`
  background-color: var(--gray-light);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.1rem;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    font-size: 1.25rem;
    background-color: ${props => props.$isLongPressed ? 'var(--secondary-color)' : 'var(--primary-light, #f0f0f0)'};
    border: 2px solid transparent;
    transform: ${props => props.$isLongPressed ? 'scale(1.1)' : 'scale(1)'};

    &:active {
      background-color: var(--primary-color);
      color: var(--white);
      transform: scale(0.95);
      border-color: var(--primary-color);
    }
  }

  &:hover {
    background-color: var(--gray-medium);

    @media (max-width: 768px) {
      background-color: var(--primary-light, #f0f0f0);
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:active {
      transform: none;
      background-color: var(--gray-light);
      color: inherit;
    }
  }
`;

const Quantity = styled.span`
  min-width: 40px;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  user-select: none;

  @media (max-width: 768px) {
    min-width: 50px;
    font-size: 1.2rem;
    padding: 0 0.5rem;
    background-color: var(--white);
    border: 2px solid var(--gray-light);
    border-radius: 8px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--danger-color, #e74c3c);
  cursor: pointer;
  padding: 0.25rem;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;

    &:active {
      background-color: rgba(231, 76, 60, 0.1);
      transform: scale(0.95);
    }
  }

  &:hover {
    color: #c0392b;

    @media (max-width: 768px) {
      background-color: rgba(231, 76, 60, 0.05);
    }
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.25rem;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 0.5rem;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;

    &:active {
      background-color: rgba(0, 128, 0, 0.1);
      transform: scale(0.95);
    }
  }

  &:hover {
    opacity: 0.75;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--gray-dark);
`;

const EmptyCartImage = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const DrawerFooter = styled.div`
  padding: 1.5rem;
  border-top: 2px solid var(--gray-medium);
  background-color: var(--gray-light);
`;

const TotalSection = styled.div`
  margin-bottom: 1rem;
`;

const TotalRow = styled.div<{ $isSubtotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.$isSubtotal ? '0.5rem' : '1rem'};
  font-size: ${props => props.$isSubtotal ? '0.95rem' : '1.25rem'};
  font-weight: ${props => props.$isSubtotal ? 'normal' : 'bold'};
  color: ${props => props.$isSubtotal ? 'var(--gray-dark)' : 'var(--text-color)'};
  padding-top: ${props => props.$isSubtotal ? '0' : '0.5rem'};
  border-top: ${props => props.$isSubtotal ? 'none' : '1px solid var(--gray-medium)'};
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 1rem;
  background-color: var(--primary-color);
  color: var(--white);
  text-align: center;
  text-decoration: none;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--secondary-color);
  }
`;

const ContinueShoppingButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem;
  background-color: transparent;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--primary-color);
    color: var(--white);
  }
`;

const BYOSectionHeader = styled.h4`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gray-dark);
  margin: 1rem 0 0.5rem 0;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--gray-light);
`;

const BYOCartItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--gray-light);

  &:last-child {
    border-bottom: none;
  }
`;

const BYOItemDetails = styled.div`
  flex: 1;
`;

const BYOItemName = styled.h4`
  font-size: 1rem;
  margin-bottom: 0.25rem;
  color: var(--text-color);
`;

const BYOItemOptions = styled.p`
  font-size: 0.8rem;
  color: var(--gray-dark);
  margin-bottom: 0.25rem;
  font-style: italic;
`;

const BYOItemPrice = styled.p`
  font-size: 1rem;
  font-weight: bold;
  color: var(--primary-color);
`;

interface SwipeableCartItemProps {
  item: any;
  rowId: string;
  onQuantityChange: (rowId: string, currentQuantity: number, delta: number) => void;
  onRemove: (rowId: string) => void;
  onEdit?: (rowId: string) => void;
  isEditable: boolean;
}

const SwipeableCartItem: React.FC<SwipeableCartItemProps> = ({ item, rowId, onQuantityChange, onRemove, onEdit, isEditable }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  // Haptic feedback helper
  const triggerHaptic = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if ('vibrate' in navigator) {
      switch(type) {
        case 'light':
          navigator.vibrate(10);
          break;
        case 'medium':
          navigator.vibrate(20);
          break;
        case 'heavy':
          navigator.vibrate(30);
          break;
      }
    }
  };

  const handlers = useSwipeable({
    onSwiping: (eventData) => {
      if (eventData.dir === 'Left') {
        const offset = Math.max(-150, Math.min(0, eventData.deltaX));
        setSwipeOffset(offset);

        // Haptic feedback when reaching delete threshold
        if (Math.abs(offset) > 100 && Math.abs(offset) < 105) {
          triggerHaptic('medium');
        }
      }
    },
    onSwipedLeft: (eventData) => {
      if (Math.abs(eventData.deltaX) > 100) {
        setIsDeleting(true);
        setSwipeOffset(-300);
        triggerHaptic('heavy');
        // Deferred by 300ms for the delete animation. Safe to keep this
        // deferred even if other rows are added/removed/reordered in the
        // meantime: onRemove resolves `rowId` against CURRENT state inside
        // its own setItems updater (see CartContext.removeCCItem), not
        // against a position captured here.
        setTimeout(() => {
          onRemove(rowId);
        }, 300);
      } else {
        setSwipeOffset(0);
      }
    },
    onSwipedRight: () => {
      setSwipeOffset(0);
    },
    onTap: () => {
      if (swipeOffset !== 0) {
        setSwipeOffset(0);
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
    trackTouch: true,
  });

  const handleQuantityChangeWithHaptic = (rowId: string, currentQuantity: number, delta: number) => {
    triggerHaptic('light');
    onQuantityChange(rowId, currentQuantity, delta);
  };

  const handleRemoveWithHaptic = (rowId: string) => {
    triggerHaptic('medium');
    onRemove(rowId);
  };

  return (
    <CartItem data-testid="cc-cart-item" $swipeOffset={swipeOffset} $isDeleting={isDeleting} {...handlers}>
      <SwipeDeleteBackground $visible={swipeOffset < -50}>
        <FiTrash2 />
      </SwipeDeleteBackground>
      <CartItemContent>
        <ItemImage
          src={
            item.product.images && item.product.images.length > 0
              ? item.product.images[0].image_url
              : 'https://images.unsplash.com/photo-1578985545062-69928b1d9587'
          }
          alt={item.product.name}
        />
        <ItemDetails>
          <ItemName>{getLocalizedField(item.product, 'name', language) as string}</ItemName>
          {item.selectedSize && (() => {
            const matchedSize = item.product.sizes?.find((s: { size_name: string }) => s.size_name === item.selectedSize);
            return (
              <ItemSize>
                {`${t('cartDrawer.sizeLabel')} ${matchedSize ? (getLocalizedField(matchedSize, 'size_name', language) as string) : item.selectedSize}`}
              </ItemSize>
            );
          })()}
          {item.selectedOptions && item.selectedOptions.length > 0 && (
            <BYOItemOptions>
              {item.selectedOptions.map((opt: SelectedOption) => getLocalizedField(opt, 'name', language) as string).join(', ')}
            </BYOItemOptions>
          )}
          {item.ccOptions && item.ccOptions.length > 0 && (
            <BYOItemOptions data-testid="cc-cart-item-options">
              {item.ccOptions.map((o: CCSelectedOption) => `${getLocalizedField(o, 'groupName', language) as string}: ${getLocalizedField(o, 'name', language) as string}`).join(', ')}
            </BYOItemOptions>
          )}
          {item.product?.has_required_options && !(item.ccOptions && item.ccOptions.length > 0) && (
            <BYOItemOptions data-testid="cart-row-needs-options" role="status">
              {t('cartDrawer.chooseOptionsHint')}
            </BYOItemOptions>
          )}
          {item.customImageUrl && (
            <CakePhotoThumb
              src={resolveUploadUrl(item.customImageUrl)}
              alt={t('productDetail.cakePhotoPreviewAlt')}
              data-testid="cc-cart-item-photo"
            />
          )}
          <ItemPrice>
            {t('cartDrawer.eachPrice', { price: formatCurrency(item.sizePrice || item.product.base_price) })}
          </ItemPrice>
          {item.quantity > 1 && (
            <ItemSubtotal>
              {t('cartDrawer.subtotalPrice', { price: formatCurrency((item.sizePrice || item.product.base_price) * item.quantity) })}
            </ItemSubtotal>
          )}
          <QuantityControls>
            <QuantityButton
              onClick={() => handleQuantityChangeWithHaptic(rowId, item.quantity, -1)}
              disabled={item.quantity <= 1}
            >
              <FiMinus />
            </QuantityButton>
            <Quantity>{item.quantity}</Quantity>
            <QuantityButton
              onClick={() => handleQuantityChangeWithHaptic(rowId, item.quantity, 1)}
            >
              <FiPlus />
            </QuantityButton>
            {isEditable && onEdit && (
              <EditButton aria-label={t('cartDrawer.editOptionsAriaLabel')} onClick={() => onEdit(rowId)}>
                <FiEdit2 />
              </EditButton>
            )}
            <RemoveButton onClick={() => handleRemoveWithHaptic(rowId)}>
              <FiTrash2 />
            </RemoveButton>
          </QuantityControls>
        </ItemDetails>
      </CartItemContent>
    </CartItem>
  );
};

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { items, removeCCItem, replaceCCItem, updateQuantity, getCartTotal, getCartCount, cartWarning, dismissCartWarning } = useCart();
  const { byoItems = [], removeBYOItem, getByoTotal } = useCCCart();
  const { t, i18n } = useTranslation();
  // Mirrors SwipeableCartItem's/LanguageToggle.tsx's pattern: i18next's
  // language can be a region-qualified tag or unset, so narrow to the two
  // v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const navigate = useNavigate();
  const [editingCCRowId, setEditingCCRowId] = useState<string | null>(null);

  const cartTotal = getCartTotal() + getByoTotal();
  const isTotalInvalid = (items.length > 0 || byoItems.length > 0) && cartTotal <= 0;

  // The row currently open in the size-edit modal, re-resolved from live
  // `items` by rowId on every render (never cached by position). If the
  // row was removed while the modal was open (e.g. by the async 404
  // rollback in CartContext), this becomes undefined and the modal below
  // simply stops rendering instead of writing onto whatever row now
  // occupies the old index.
  const editingItem = editingCCRowId !== null
    ? items.find(item => item.rowId === editingCCRowId)
    : undefined;

  const handleQuantityChange = (rowId: string, currentQuantity: number, delta: number) => {
    const newQuantity = currentQuantity + delta;
    if (newQuantity > 0) {
      updateQuantity(rowId, newQuantity);
    }
  };

  const handleEdit = (rowId: string) => {
    setEditingCCRowId(rowId);
  };

  const handleEditConfirm = (newCcOptions: CCSelectedOption[], newPrice: number) => {
    if (editingCCRowId === null || !editingItem) return;
    replaceCCItem(editingCCRowId, {
      ...editingItem,
      ccOptions: newCcOptions,
      sizePrice: newPrice,
    });
    setEditingCCRowId(null);
  };

  const handleEditClose = () => {
    setEditingCCRowId(null);
  };

  const handleEditBYOItem = (byoItem: BYOCartItem) => {
    navigate('/custom-cakes/build-your-own', { state: { editBYOItem: byoItem } });
    onClose();
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <DrawerContainer $isOpen={isOpen}>
        <DrawerHeader>
          <DrawerTitle>{t('cartDrawer.title', { count: items.length + byoItems.length })}</DrawerTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          {cartWarning && (
            <WarningBanner role="alert">
              <span>{cartWarning}</span>
              <WarningBannerDismiss aria-label={t('cartDrawer.dismissWarning')} onClick={dismissCartWarning}>
                <FiX />
              </WarningBannerDismiss>
            </WarningBanner>
          )}
          {items.length === 0 && byoItems.length === 0 ? (
            <EmptyCart>
              <EmptyCartImage>🛒</EmptyCartImage>
              <h3>{t('cartDrawer.emptyTitle')}</h3>
              <p>{t('cartDrawer.emptySubtitle')}</p>
            </EmptyCart>
          ) : (
            <>
              {items.length > 0 && (
                <>
                  <SwipeHint>
                    {t('cartDrawer.swipeHint')}
                  </SwipeHint>
                  {items.map((item) => (
                    <SwipeableCartItem
                      key={item.rowId}
                      item={item}
                      rowId={item.rowId}
                      onQuantityChange={handleQuantityChange}
                      onRemove={removeCCItem}
                      onEdit={handleEdit}
                      isEditable={(item.ccOptions?.length ?? 0) > 0 || !!item.product?.has_selectable_options || !!item.product?.has_required_options}
                    />
                  ))}
                </>
              )}
              {byoItems.length > 0 && (
                <>
                  <BYOSectionHeader>{t('cartDrawer.byoSectionHeader')}</BYOSectionHeader>
                  {byoItems.map((byoItem: BYOCartItem) => (
                    <BYOCartItemRow key={byoItem.byoId} data-testid="byo-cart-item">
                      <BYOItemDetails>
                        <BYOItemName>{t('cartDrawer.byoItemName')}</BYOItemName>
                        {byoItem.selectedOptions.length > 0 && (
                          <BYOItemOptions>
                            {byoItem.selectedOptions.map(opt => getLocalizedField(opt, 'name', language) as string).join(', ')}
                          </BYOItemOptions>
                        )}
                        <BYOItemPrice>{formatCurrency(byoItem.totalPrice)}</BYOItemPrice>
                      </BYOItemDetails>
                      <EditButton
                        aria-label={t('cartDrawer.editByoAriaLabel')}
                        onClick={() => handleEditBYOItem(byoItem)}
                      >
                        <FiEdit2 />
                      </EditButton>
                      <RemoveButton
                        aria-label={t('cartDrawer.removeByoAriaLabel')}
                        onClick={() => removeBYOItem(byoItem.byoId)}
                      >
                        <FiTrash2 />
                      </RemoveButton>
                    </BYOCartItemRow>
                  ))}
                </>
              )}
            </>
          )}
        </DrawerContent>

        {editingItem && (
          <CCOptionsEditModal
            item={editingItem}
            onConfirm={handleEditConfirm}
            onClose={handleEditClose}
          />
        )}

        {(items.length > 0 || byoItems.length > 0) && (
          <DrawerFooter>
            {isTotalInvalid && (
              <WarningBanner role="alert" data-testid="cart-invalid-total-banner">
                <span>{t('cartDrawer.invalidTotalBanner')}</span>
              </WarningBanner>
            )}
            <TotalSection>
              <TotalRow $isSubtotal>
                <span>{t('cartDrawer.itemsCountLabel', { count: getCartCount() + byoItems.length })}</span>
                <span>{formatCurrency(cartTotal)}</span>
              </TotalRow>
              <TotalRow>
                <span>{t('cartDrawer.totalLabel')}</span>
                <span>{formatCurrency(cartTotal)}</span>
              </TotalRow>
            </TotalSection>
            <CheckoutButton to="/checkout" onClick={onClose}>
              {t('cartDrawer.proceedToCheckout')}
            </CheckoutButton>
            <ContinueShoppingButton onClick={() => { navigate('/custom-cakes'); onClose(); }}>
              {t('cartDrawer.continueShopping')}
            </ContinueShoppingButton>
          </DrawerFooter>
        )}
      </DrawerContainer>
    </>
  );
};

export default CartDrawer;